/**
 * Content Security Policy and XSS Protection
 * Provides runtime security controls and content sanitization
 */

import { debugLog, errorLog } from "../Helpers/LoggingUtils";

/**
 * Content Security Policy configuration
 * This should be implemented at the server level, but we provide client-side enforcement as backup
 * Configured for React applications with Firebase and external service connectivity
 */
const CSP_CONFIG = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'unsafe-inline'", "'unsafe-eval'", "https:", "http:"], // React needs unsafe-eval for development
  'style-src': ["'self'", "'unsafe-inline'", "https://fonts.googleapis.com", "https:", "http:"],
  'font-src': ["'self'", "https://fonts.gstatic.com", "https:", "http:", "data:"],
  'img-src': ["'self'", "data:", "https:", "http:", "blob:"],
  'connect-src': [
    "'self'", 
    "https:", "http:", "wss:", "ws:",
    // Firebase services
    "https://api.firebase.googleapis.com",
    "https://firestore.googleapis.com",
    "https://firebase.googleapis.com",
    "https://identitytoolkit.googleapis.com",
    "https://securetoken.googleapis.com",
    "https://firebasestorage.googleapis.com",
    "https://www.googleapis.com",
    // Firebase Cloud Functions (all regions)
    "https://*.cloudfunctions.net",
    // Firebase Realtime Database
    "https://*.firebaseio.com",
    "wss://*.firebaseio.com",
    // Third-party APIs
    "https://api.openai.com",
    "https://api.anthropic.com",
    "https://api.azure.com",
    "https://management.azure.com",
    "https://www.google.com"
  ],
  'media-src': ["'self'", "data:", "blob:", "https:", "http:"],
  'frame-src': [
    "'self'", 
    "https://www.google.com",
    "https://accounts.google.com",
    "https://www.googleapis.com",
    "https://firebase.google.com",
    "https://console.firebase.google.com",
    "https://www.youtube.com",
    "https://maps.google.com",
    "https://calendar.google.com",
    "https://docs.google.com",
    "https:", // Allow all HTTPS frames for flexibility
    "data:", "blob:"
  ],
  'object-src': ["'none'"],
  'base-uri': ["'self'"],
  'form-action': ["'self'", "https:"],
  // Note: 'frame-ancestors' is not supported in CSP meta tags, only in HTTP headers
  'upgrade-insecure-requests': [],
  'worker-src': ["'self'", "blob:"], // For service workers
  'manifest-src': ["'self'"] // For PWA manifest
};

/**
 * Generates CSP header string
 */
const generateCSPHeader = () => {
  return Object.entries(CSP_CONFIG)
    .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
    .join('; ');
};

/**
 * Applies CSP via meta tag (client-side enforcement)
 * This is a fallback when server-side CSP headers are not available
 */
const applyClientSideCSP = () => {
  try {
    // Check if CSP meta tag already exists
    let cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    
    if (!cspMeta) {
      // Create new CSP meta tag
      cspMeta = document.createElement('meta');
      cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
      document.head.appendChild(cspMeta);
    }
    
    // Set the CSP content
    const cspContent = generateCSPHeader();
    cspMeta.setAttribute('content', cspContent);
    
    debugLog('[SECURITY] Client-side CSP applied:', cspContent);
    
    return true;
  } catch (error) {
    errorLog('[SECURITY] Failed to apply client-side CSP:', error);
    return false;
  }
};

/**
 * Initializes CSP protection
 */
const initializeCSP = () => {
  // Apply CSP immediately if possible
  try {
    if (document.head) {
      applyClientSideCSP();
    } else {
      // If head is not ready, wait for DOM
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', applyClientSideCSP);
      } else {
        // DOM is ready but head might not be available yet
        setTimeout(applyClientSideCSP, 0);
      }
    }
  } catch (error) {
    errorLog('[SECURITY] Failed to initialize CSP:', error);
  }
};

/**
 * Debug function to check CSP status
 */
const debugCSP = () => {
  const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  const cspContent = cspMeta ? cspMeta.getAttribute('content') : 'Not found';
  
  debugLog('[SECURITY] CSP Meta Tag:', cspMeta ? 'Found' : 'Not Found');
  debugLog('[SECURITY] CSP Content:', cspContent);
  debugLog('[SECURITY] Expected CSP:', generateCSPHeader());

  // Check if frame-src is included
  const hasFrameSrc = cspContent.includes('frame-src');
  debugLog('[SECURITY] Frame-src included:', hasFrameSrc);

  return {
    hasCSP: !!cspMeta,
    content: cspContent,
    hasFrameSrc
  };
};

/**
 * HTML sanitization with DOMPurify-like functionality
 */
class HTMLSanitizer {
  static ALLOWED_TAGS = [
    'div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'strong', 'em', 'b', 'i', 'u', 'br', 'hr',
    'ul', 'ol', 'li', 'dl', 'dt', 'dd',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'blockquote', 'pre', 'code',
    'a', 'img'
  ];

  static ALLOWED_ATTRIBUTES = {
    'a': ['href', 'title', 'target'],
    'img': ['src', 'alt', 'title', 'width', 'height'],
    '*': ['class', 'id', 'style'] // Global attributes (use with caution)
  };

  static ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

  /**
   * Sanitizes HTML content
   */
  static sanitize(html) {
    if (typeof html !== 'string') return '';

    // Remove script tags and their content
    html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    // Remove dangerous tags
    const dangerousTags = ['script', 'iframe', 'object', 'embed', 'applet', 'meta', 'link'];
    dangerousTags.forEach(tag => {
      const regex = new RegExp(`<\\/?${tag}\\b[^>]*>`, 'gi');
      html = html.replace(regex, '');
    });

    // Remove javascript: and data: protocols (except safe data: URIs)
    html = html.replace(/javascript:/gi, '');
    html = html.replace(/data:(?!image\/(png|jpg|jpeg|gif|svg|webp))[^;,]*[;,]/gi, '');

    // Remove on* event handlers
    html = html.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');

    // Validate and sanitize URLs
    html = html.replace(/href\s*=\s*["']([^"']*)["']/gi, (match, url) => {
      const sanitizedUrl = this.sanitizeURL(url);
      return `href="${sanitizedUrl}"`;
    });

    html = html.replace(/src\s*=\s*["']([^"']*)["']/gi, (match, url) => {
      const sanitizedUrl = this.sanitizeURL(url);
      return `src="${sanitizedUrl}"`;
    });

    // Remove style attributes with dangerous content
    html = html.replace(/style\s*=\s*["']([^"']*)["']/gi, (match, style) => {
      const sanitizedStyle = this.sanitizeStyle(style);
      return sanitizedStyle ? `style="${sanitizedStyle}"` : '';
    });

    return html;
  }

  /**
   * Sanitizes URLs
   */
  static sanitizeURL(url) {
    if (!url || typeof url !== 'string') return '';

    // Allow relative URLs
    if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
      return url;
    }

    // Allow anchor links
    if (url.startsWith('#')) {
      return url;
    }

    try {
      const urlObj = new URL(url);
      
      if (!this.ALLOWED_PROTOCOLS.includes(urlObj.protocol)) {
        return '';
      }

      return url;
    } catch (error) {
      return '';
    }
  }

  /**
   * Sanitizes CSS styles
   */
  static sanitizeStyle(style) {
    if (!style || typeof style !== 'string') return '';

    // Remove dangerous CSS functions and properties
    const dangerousPatterns = [
      /expression\s*\(/gi,
      /javascript\s*:/gi,
      /vbscript\s*:/gi,
      /data\s*:/gi,
      /import\s*\(/gi,
      /url\s*\(\s*["']?javascript:/gi,
      /@import\s+/gi,
      /behavior\s*:/gi,
      /binding\s*:/gi
    ];

    for (const pattern of dangerousPatterns) {
      style = style.replace(pattern, '');
    }

    return style;
  }
}

/**
 * XSS Protection utilities
 */
class XSSProtection {
  /**
   * Escapes HTML entities
   */
  static escapeHTML(text) {
    if (typeof text !== 'string') return text;

    const entityMap = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '/': '&#x2F;',
      '`': '&#x60;',
      '=': '&#x3D;'
    };

    return text.replace(/[&<>"'`=/]/g, (char) => entityMap[char]);
  }

  /**
   * Validates and sanitizes user input
   */
  static sanitizeInput(input, options = {}) {
    if (typeof input !== 'string') return input;

    const {
      allowHTML = false,
      maxLength = 10000,
      allowedChars = null
    } = options;

    // Trim whitespace
    let sanitized = input.trim();

    // Check maximum length
    if (sanitized.length > maxLength) {
      throw new Error(`Input exceeds maximum length of ${maxLength} characters`);
    }

    // Filter allowed characters if specified
    if (allowedChars) {
      const regex = new RegExp(`[^${allowedChars}]`, 'g');
      sanitized = sanitized.replace(regex, '');
    }

    // HTML handling
    if (allowHTML) {
      sanitized = HTMLSanitizer.sanitize(sanitized);
    } else {
      sanitized = this.escapeHTML(sanitized);
    }

    return sanitized;
  }

  /**
   * Validates JSON input to prevent prototype pollution
   */
  static sanitizeJSON(jsonString) {
    if (typeof jsonString !== 'string') return jsonString;

    try {
      // Parse JSON with prototype pollution protection
      const parsed = JSON.parse(jsonString, (key, value) => {
        // Block dangerous keys
        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
          return undefined;
        }
        return value;
      });

      return parsed;
    } catch (error) {
      throw new Error('Invalid JSON input');
    }
  }
}

/**
 * Runtime security checks
 */
class SecurityMonitor {
  static violations = [];
  static maxViolations = 100;

  /**
   * Reports security violations
   */
  static reportViolation(type, message, context = {}) {
    const violation = {
      type,
      message,
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    this.violations.push(violation);

    // Keep only recent violations
    if (this.violations.length > this.maxViolations) {
      this.violations = this.violations.slice(-this.maxViolations);
    }

    // Log violation
    console.warn(`[SECURITY VIOLATION] ${type}: ${message}`, context);

    // In production, send to monitoring service
    if (process.env.NODE_ENV === 'production') {
      this.sendViolationReport(violation);
    }
  }

  /**
   * Sends violation report to monitoring service
   */
  static sendViolationReport(violation) {
    // Implementation would send to your security monitoring service
    // Example: Sentry, LogRocket, or custom endpoint
    try {
      // fetch('/api/security/violations', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(violation)
      // });
    } catch (error) {
      console.error('Failed to send security violation report:', error);
    }
  }

  /**
   * Validates component props for security issues
   */
  static validateComponentProps(componentType, props) {
    if (!props || typeof props !== 'object') return props;

    // Check for dangerous props
    const dangerousProps = ['dangerouslySetInnerHTML', 'ref'];
    
    for (const prop of dangerousProps) {
      if (prop in props) {
        this.reportViolation('DANGEROUS_PROP', `Component ${componentType} uses dangerous prop: ${prop}`, {
          componentType,
          prop
        });
      }
    }

    // Sanitize string props
    const sanitizedProps = {};
    for (const [key, value] of Object.entries(props)) {
      if (typeof value === 'string') {
        sanitizedProps[key] = XSSProtection.sanitizeInput(value, { allowHTML: false });
      } else {
        sanitizedProps[key] = value;
      }
    }

    return sanitizedProps;
  }
}

/**
 * Secure random token generation
 */
class TokenGenerator {
  /**
   * Generates cryptographically secure random string
   */
  static generateSecureToken(length = 32) {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generates CSRF token
   */
  static generateCSRFToken() {
    return this.generateSecureToken(32);
  }

  /**
   * Generates session ID
   */
  static generateSessionId() {
    return this.generateSecureToken(48);
  }
}

export {
  CSP_CONFIG,
  generateCSPHeader,
  applyClientSideCSP,
  initializeCSP,
  debugCSP,
  HTMLSanitizer,
  XSSProtection,
  SecurityMonitor,
  TokenGenerator
};